import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { PageHero } from '@/components/ui/PageHero'
import { StatePill } from '@/components/ui/StatePill'
import { IconBadge, type IconName } from '@/components/ui/Icons'
import { Eyebrow } from '@/components/ui/Section'
import { useScrollSpy } from '@/hooks/useSectionProgress'
import { Stagger } from '@/components/motion/Reveal'
import { useDocumentMeta } from '@/lib/seo'
import { PARTNER_CONTACT_PATH, ROUTES } from '@/lib/site'

const TOTAL_LAYERS = 9

const JUMP_LINKS = [
  { id: 'overview', label: 'Overview' },
  { id: 'finance', label: 'Finance' },
  { id: 'sales', label: 'Sales' },
  { id: 'marketing', label: 'Marketing & Growth' },
  { id: 'operations', label: 'Operations' },
  { id: 'supply', label: 'Supply chain' },
  { id: 'compliance', label: 'Compliance' },
]

function SectionIntro({
  group,
  name,
  id,
  icon,
  children,
  badge,
}: {
  group: string
  name: string
  id: string
  icon: IconName
  children: React.ReactNode
  badge?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="max-w-2xl">
        <div className="flex items-center gap-4">
          <IconBadge
            name={icon}
            tone={
              group === 'Core'
                ? 'primary'
                : group === 'Delivery'
                  ? 'violet'
                  : group === 'Governance'
                    ? 'computes'
                    : 'projected'
            }
          />
          <Eyebrow className="text-primary">Group · {group}</Eyebrow>
        </div>
        <h2
          id={`${id}-heading`}
          className="mt-5 text-[28px] font-extrabold leading-[1.12] tracking-[-0.025em] sm:text-[36px]"
        >
          {name}
        </h2>
        <div className="mt-5 grid gap-4">{children}</div>
      </div>
      {badge ? <div className="flex-none">{badge}</div> : null}
    </div>
  )
}

/** A definition list of panels and their state, used by Sales and Compliance. */
function PanelList({
  items,
}: {
  items: { name: string; requires?: string; state: 'computes' | 'locked' }[]
}) {
  return (
    <dl className="grid gap-3">
      {items.map((item) => (
        <div
          key={item.name}
          className="flex flex-wrap items-start justify-between gap-3 card-spot rounded-card border border-line bg-white/85 backdrop-blur p-5 shadow-card"
        >
          <dt className="min-w-0">
            <span className="block text-[14.5px] font-bold tracking-[-0.01em]">{item.name}</span>
            {item.requires ? (
              <span className="mt-1 block text-[13px] leading-[1.6] text-muted">
                {item.requires}
              </span>
            ) : null}
          </dt>
          <dd className="flex-none">
            <StatePill tone={item.state === 'computes' ? 'computes' : 'locked'}>
              {item.state}
            </StatePill>
          </dd>
        </div>
      ))}
    </dl>
  )
}

const JUMP_IDS = JUMP_LINKS.map((link) => link.id)

export default function SectionsPage() {
  useDocumentMeta(ROUTES.sections)
  // Highlights whichever section the reader is currently inside.
  const activeId = useScrollSpy(JUMP_IDS)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="sections">
        <PageHero
          crumb="Seven sections"
          title="Seven sections. Section depth follows data availability, not ambition."
          lede="Finance is safe: a trial balance gives it. Supply chain needs a purchase-order register and a goods-receipt log. Compliance needs a filing calendar. Below is every section, with what it computes on an accounting export alone and what each other panel is waiting for."
        />
        <nav aria-label="Sections on this page" className="mt-12">
          <Stagger
            variant="up"
            step={55}
            as="ul"
            itemAs="li"
            className="flex flex-wrap justify-center gap-2"
          >
            {JUMP_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                aria-current={activeId === link.id ? 'true' : undefined}
                className={
                  'group relative inline-flex min-h-[42px] items-center overflow-hidden rounded-control border px-4 text-[13px] font-semibold transition-all duration-300 ease-brand ' +
                  (activeId === link.id
                    ? 'border-primary/35 bg-primary/10 text-primary shadow-card'
                    : 'border-line bg-white/80 text-muted backdrop-blur hover:border-edge-soft hover:text-ink')
                }
              >
                {link.label}
                <span
                  aria-hidden="true"
                  className={
                    'absolute inset-x-0 bottom-0 h-[2px] origin-left bg-gradient-to-r from-primary to-violet transition-transform duration-500 ease-brand ' +
                    (activeId === link.id ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100')
                  }
                />
              </a>
            ))}
          </Stagger>
        </nav>
      </Band>

      {/* Overview */}
      <Band layer={2} total={TOTAL_LAYERS} tone="warm" id="overview" labelledBy="overview-heading">
        <SectionIntro group="Company" name="Overview" icon="overview" id="overview">
          <p className="text-[15px] leading-[1.75] text-muted">
            The figures checked first, and the signals ranked against them. A signal is not a
            generated observation — it carries a severity, a window, the metrics it refers to and
            its evidence rows, each with the source behind it.
          </p>
          <p className="text-[15px] leading-[1.75] text-muted">
            Severities are deliberately three: needs action, watch, working. Anything finer would
            imply a precision the underlying reading does not have.
          </p>
        </SectionIntro>
        <Stagger
          variant="up"
          step={90}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 lg:grid-cols-3"
        >
          {[
            {
              rank: '1',
              name: 'Needs action',
              meta: '4 qtrs',
              detail:
                'Ranked first, with evidence rows keyed to the documents they were read from.',
            },
            {
              rank: '2',
              name: 'Watch',
              meta: 'projected',
              detail:
                'A signal on a projected window says so, so it is never mistaken for something recorded.',
            },
            {
              rank: '3',
              name: 'Working',
              meta: '2 qtrs',
              detail:
                'What is going right is reported too. A list of only problems is a biased reading.',
            },
          ].map((signal) => (
            <div
              key={signal.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 font-mono text-[11px] text-primary">
                  {signal.rank}
                </span>
                <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">{signal.name}</h3>
                <StatePill className="ml-auto">{signal.meta}</StatePill>
              </div>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{signal.detail}</p>
            </div>
          ))}
        </Stagger>
      </Band>

      {/* Finance */}
      <Band layer={3} total={TOTAL_LAYERS} tone="ice" id="finance" labelledBy="finance-heading">
        <SectionIntro
          group="Core"
          name="Finance"
          icon="finance"
          id="finance"
          badge={<StatePill tone="computes">real on one export</StatePill>}
        >
          <p className="text-[15px] leading-[1.75] text-muted">
            The section a trial balance alone makes real. Revenue, margin, cost and working capital
            derive from measures a general ledger genuinely states, which is why this is where a
            first upload is judged.
          </p>
        </SectionIntro>
        <Stagger
          variant="up"
          step={90}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 lg:grid-cols-3"
        >
          {[
            {
              name: 'Formula, visible',
              detail:
                'Each panel states its formula as a tree over named measures, not as prose. Substituting the measures reproduces the figure exactly.',
            },
            {
              name: 'Drivers, with residual',
              detail:
                'A movement is decomposed into drivers, and whatever the drivers do not explain is labelled unattributed instead of being distributed across them.',
            },
            {
              name: 'Line items, with "other"',
              detail:
                'Breakdowns carry an explicit other bucket rather than quietly dropping the tail.',
            },
          ].map((item) => (
            <div
              key={item.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">{item.name}</h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{item.detail}</p>
            </div>
          ))}
        </Stagger>
      </Band>

      {/* Sales */}
      <Band layer={4} total={TOTAL_LAYERS} tone="mint" id="sales" labelledBy="sales-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <SectionIntro group="Core" name="Sales" icon="sales" id="sales">
            <p className="text-[15px] leading-[1.75] text-muted">
              Sales invoiced is in the ledger. Sales as a pipeline — stages, conversion, cycle
              length, win rate — is in a CRM, and no accounting export contains it.
            </p>
            <p className="text-[15px] leading-[1.75] text-muted">
              Send a pipeline export and the section fills. Until then it shows what the ledger
              supports and names the rest.
            </p>
          </SectionIntro>
          <PanelList
            items={[
              { name: 'Revenue by period', state: 'computes' },
              { name: 'Pipeline coverage', requires: 'needs a CRM stage export', state: 'locked' },
              {
                name: 'Win rate',
                requires: 'needs won and lost outcomes per opportunity',
                state: 'locked',
              },
              {
                name: 'Sales cycle length',
                requires: 'needs created and closed dates',
                state: 'locked',
              },
            ]}
          />
        </div>
      </Band>

      {/* Marketing */}
      <Band
        layer={5}
        total={TOTAL_LAYERS}
        tone="warm"
        id="marketing"
        labelledBy="marketing-heading"
      >
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <SectionIntro group="Core" name="Marketing & Growth" icon="marketing" id="marketing">
            <p className="text-[15px] leading-[1.75] text-muted">
              Marketing spend sits in the ledger and computes. What that spend caused does not, and
              attribution invented from a ledger is the clearest example of a number that looks real
              and is not.
            </p>
            <p className="text-[15px] leading-[1.75] text-muted">
              Cost per acquisition needs acquisitions counted somewhere other than the accounts.
              That is a source, not a model.
            </p>
          </SectionIntro>
          <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-8">
            <h3 className="text-[17px] font-extrabold tracking-[-0.015em]">
              The temptation this section exists to refuse
            </h3>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              Every analytics product can produce a customer acquisition cost from a spend line and
              a customer count guessed from invoice frequency. It renders beautifully and it is not
              a measurement.
            </p>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              A locked panel with a named requirement is worth more, because it tells you exactly
              which export makes it real.
            </p>
          </div>
        </div>
      </Band>

      {/* Operations */}
      <Band
        layer={6}
        total={TOTAL_LAYERS}
        tone="ice"
        id="operations"
        labelledBy="operations-heading"
      >
        <SectionIntro group="Delivery" name="Operations" icon="operations" id="operations">
          <p className="sr-only">Operations panels and what unlocks them.</p>
        </SectionIntro>
        <Stagger
          variant="up"
          step={100}
          as="ul"
          itemAs="li"
          className="mt-8 grid gap-4 lg:grid-cols-2"
        >
          {[
            {
              name: 'Utilisation',
              detail:
                'Output against available capacity. Capacity is an operational fact about plant and shifts; no accounting export states it.',
              unlock:
                'Unlocked by a stated line or shift capacity. Note: rising utilisation is treated as bad here — past a point it means no headroom, which is where delivery failures start.',
            },
            {
              name: 'Rework rate',
              detail:
                'Needs a quality outcome per unit produced. A ledger may carry a rework cost, but not the denominator.',
              unlock: 'Unlocked by a quality or inspection log.',
            },
          ].map((panel) => (
            <div
              key={panel.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">{panel.name}</h3>
                <StatePill tone="locked">locked</StatePill>
              </div>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{panel.detail}</p>
              <p className="mt-3 border-t border-line pt-3 text-[13px] leading-[1.7] text-muted-soft">
                {panel.unlock}
              </p>
            </div>
          ))}
        </Stagger>
      </Band>

      {/* Supply chain */}
      <Band layer={7} total={TOTAL_LAYERS} tone="mint" id="supply" labelledBy="supply-heading">
        <SectionIntro group="Delivery" name="Supply chain" icon="supply" id="supply">
          <p className="text-[15px] leading-[1.75] text-muted">
            One panel computes today. The other three name what they need, and one of them is the
            closest locked panel in the whole product.
          </p>
        </SectionIntro>
        <Stagger
          variant="up"
          step={90}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 sm:grid-cols-2"
        >
          {[
            {
              name: 'Inventory cover',
              state: 'computes' as const,
              label: 'computes',
              detail:
                'Closing inventory over cost of goods sold, in days of the period. The one supply-chain panel a trial balance genuinely supports.',
            },
            {
              name: 'Vendor concentration',
              state: 'locked' as const,
              label: 'closest',
              detail:
                'The share of purchases going to the largest vendors, so it needs purchases by vendor. Mapping every creditor account individually would give this.',
            },
            {
              name: 'Lead time',
              state: 'locked' as const,
              label: 'locked',
              detail:
                'Order date to receipt date. Neither is in a ledger. Unlocked by a purchase-order register with receipts.',
            },
            {
              name: 'Cost variance to plan',
              state: 'locked' as const,
              label: 'locked',
              detail:
                'A variance needs a plan. The ledger records what was spent, never what was budgeted. Unlocked by a budget or standard-cost schedule.',
            },
          ].map((panel) => (
            <div
              key={panel.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">{panel.name}</h3>
                <StatePill tone={panel.state === 'computes' ? 'computes' : 'locked'}>
                  {panel.label}
                </StatePill>
              </div>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{panel.detail}</p>
            </div>
          ))}
        </Stagger>
      </Band>

      {/* Compliance */}
      <Band
        layer={8}
        total={TOTAL_LAYERS}
        tone="warm"
        id="compliance"
        labelledBy="compliance-heading"
      >
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <SectionIntro group="Governance" name="Compliance" icon="compliance" id="compliance">
            <p className="text-[15px] leading-[1.75] text-muted">
              Entirely document arithmetic. Filings on time is a comparison of due dates against
              filed dates; findings and their ageing come from an audit observation register;
              renewal exposure needs contract end dates and values.
            </p>
            <p className="text-[15px] leading-[1.75] text-muted">
              None of it can be derived from a ledger, and all of it is straightforward once the
              register exists.
            </p>
          </SectionIntro>
          <PanelList
            items={[
              {
                name: 'Filings on time',
                requires: 'filing calendar, or GST acknowledgements',
                state: 'locked',
              },
              { name: 'Open findings', requires: 'audit observation register', state: 'locked' },
              {
                name: 'Days to close a finding',
                requires: 'register with opened and closed dates',
                state: 'locked',
              },
              {
                name: 'Contract renewal exposure',
                requires: 'contract register with values and end dates',
                state: 'locked',
              },
            ]}
          />
        </div>
      </Band>

      <Band layer={9} total={TOTAL_LAYERS} tone="ice" labelledBy="sections-cta" showMark={false}>
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="sections-cta"
            className="text-balance text-[26px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
          >
            Which sections do you actually have data for?
          </h2>
          <p className="mx-auto mt-5 max-w-[58ch] text-pretty text-[15px] leading-[1.75] text-muted">
            That question decides where we build first. Tell us what exports exist and we will say
            plainly which sections would be real for you and which would stay locked.
          </p>
          <div className="mt-9 flex justify-center">
            <ButtonLink to={PARTNER_CONTACT_PATH} size="lg">
              Start that conversation
            </ButtonLink>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
